import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Link, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import MapsUgcIcon from '@mui/icons-material/MapsUgc';
import { Login } from './Login.jsx';
import { logout as apiLogout, setAuthToken } from './api';

const PersonasPage = lazy(() => import('./components/PersonasPage'));
const UserPage = lazy(() => import('./components/UserPage'));
const AnalyticsPage = lazy(() => import('./components/AnalyticsPage.jsx'));
const TariffsPage = lazy(() => import('./components/TariffsPage'));
const FeedbackPage = lazy(() => import('./components/FeedbackPage.jsx'));
const PaymentReconciliationPage = lazy(() => import('./components/PaymentReconciliationPage.jsx'));

const PageLoader = () => (
  <Box display="flex" justifyContent="center" py={6}>
    <CircularProgress />
  </Box>
);

const AdminShell = ({ onLogout }) => {
  const navigate = useNavigate();

  const handleLogout = () => {
    onLogout();
    navigate('/login', { replace: true });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, width: '100%', maxWidth: 'none', boxSizing: 'border-box' }}>
      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
        <MapsUgcIcon fontSize="medium" />
        Админ панель PsysimBot
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button component={Link} to="/personas" variant="outlined">Персоны</Button>
          <Button component={Link} to="/users" variant="outlined">Пользователи</Button>
          <Button component={Link} to="/analytics" variant="outlined">Аналитика</Button>
          <Button component={Link} to="/tariffs" variant="outlined">Тарифы</Button>
          <Button component={Link} to="/feedback" variant="outlined">Фидбеки</Button>
          <Button component={Link} to="/payment-reconciliation" variant="outlined">Сверка платежей</Button>
        </Box>
        <Button variant="contained" color="secondary" onClick={handleLogout} size="small">
          Logout
        </Button>
      </Box>

      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Navigate to="/analytics" replace />} />
          <Route path="/personas" element={<PersonasPage />} />
          <Route path="/users" element={<UserPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/tariffs" element={<TariffsPage />} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/payment-reconciliation" element={<PaymentReconciliationPage />} />
          <Route path="*" element={<Navigate to="/analytics" replace />} />
        </Routes>
      </Suspense>
    </Box>
  );
};

const LoginRoutes = ({ onLogin }) => (
  <Routes>
    <Route path="/login" element={<Login onLogin={onLogin} />} />
    <Route path="*" element={<Login onLogin={onLogin} />} />
  </Routes>
);

const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!localStorage.getItem('accessToken'));

  const handleLogin = (username, accessToken, tokenType = 'bearer') => {
    localStorage.setItem('username', username);
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('tokenType', tokenType);
    setAuthToken(accessToken, tokenType);
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    apiLogout();
    setIsLoggedIn(false);
  };

  useEffect(() => {
    const storedToken = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'bearer';
    if (storedToken) {
      setAuthToken(storedToken, tokenType);
      setIsLoggedIn(true);
    }

    const handleAuthLogout = () => setIsLoggedIn(false);
    window.addEventListener('auth:logout', handleAuthLogout);
    return () => window.removeEventListener('auth:logout', handleAuthLogout);
  }, []);

  return (
    <Router>
      {isLoggedIn ? (
        <AdminShell onLogout={handleLogout} />
      ) : (
        <LoginRoutes onLogin={handleLogin} />
      )}
    </Router>
  );
};

export default App;
